import React from 'react';
import './Skeleton.css';

/**
 * Loading placeholders. Build a page's own skeleton from SkeletonLine / SkeletonBlock, or use
 * PageSkeleton for a generic page. Pass `delayed` to the outermost one so a load that finishes
 * within 150ms never flashes it.
 */
export const SkeletonLine = ({ width = '100%', height, style }) => (
  <span className="sk-line" style={{ width, ...(height ? { height } : null), ...style }} aria-hidden="true" />
);

export const SkeletonBlock = ({ height = '4rem', width = '100%', style }) => (
  <span className="sk-block" style={{ width, height, ...style }} aria-hidden="true" />
);

/** A table-shaped placeholder: a header row and `rows` body rows of `cols` cells. */
export const SkeletonTable = ({ rows = 6, cols = 5 }) => (
  <div className="page-skeleton__table" aria-hidden="true">
    {Array.from({ length: rows + 1 }, (_, r) => (
      <div className="sk-row" key={r}>
        {Array.from({ length: cols }, (__, c) => (
          <SkeletonLine key={c} width={`${c === 0 ? 18 : 100 / cols}%`} height={r === 0 ? '0.7rem' : '0.9rem'} />
        ))}
      </div>
    ))}
  </div>
);

/** A table placeholder for a section or card whose rows are loading. */
export const SkeletonPanel = ({ rows = 5, cols = 6, label = 'Loading' }) => (
  <div className="sk-panel sk-delayed" role="status" aria-label={label}>
    <SkeletonTable rows={rows} cols={cols} />
  </div>
);

/** A few lines of text, for a small block (a settings card, a hint) that is loading. */
export const SkeletonText = ({ lines = 3, label = 'Loading' }) => (
  <div className="sk-stack sk-panel sk-delayed" role="status" aria-label={label}>
    {Array.from({ length: lines }, (_, i) => (
      <SkeletonLine key={i} width={['55%', '85%', '70%', '40%'][i % 4]} />
    ))}
  </div>
);

/** Generic page placeholder: title, a filter row and a couple of cards. */
export const PageSkeleton = ({ label = 'Loading' }) => (
  <div className="page-skeleton sk-delayed" role="status" aria-label={label}>
    <SkeletonLine width="14rem" height="1.3rem" />
    <div className="page-skeleton__filters">
      {[0, 1, 2, 3].map((i) => <SkeletonBlock key={i} height="2.1rem" />)}
    </div>
    <div className="sk-card">
      <SkeletonTable rows={6} cols={6} />
    </div>
    <div className="sk-card sk-stack">
      <SkeletonLine width="40%" />
      <SkeletonLine width="85%" />
      <SkeletonLine width="70%" />
    </div>
  </div>
);

export default PageSkeleton;
